// src/components/Expenses/ExpensesView.js
import { useEffect, useMemo, useRef, useState } from "react";
import ExpenseForm from "./ExpenseForm";
import { db } from "../../firebase";
import { deleteExpenseSupport, getExpenseSupportUrl } from '../../lib/cloudinaryApi';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
   getDoc,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { logAudit } from '../../lib/audit';
import { useExpensesData } from "../../hooks/useExpensesData";
import { markExpensesCacheDirty } from "../../lib/expensesCache";
import { hasAccess } from "../../lib/modules";

// Formato COP sin decimales
const fmtCOP = (n) =>
  Number(n || 0).toLocaleString("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 });

const fmtDateCell = (e) => {
  if (e.dateStr) return e.dateStr;
  const d = e.date?.toDate ? e.date.toDate() : (e.date ? new Date(e.date) : null);
  return d ? d.toISOString().slice(0, 10) : "";
};

// Normaliza una fecha a YMD local
const toLocalYMD = (value) => {
  const d = value instanceof Date ? value : (value ? new Date(value) : new Date());
  if (Number.isNaN(d.getTime())) return "";
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const getExpenseYMD = (e) => {
  if (e.dateStr) return e.dateStr;
  const d = e.date?.toDate ? e.date.toDate() : (e.date ? new Date(e.date) : null);
  return d ? toLocalYMD(d) : "";
};

// La fecha del gasto es una fecha de negocio. Preferimos dateStr y, por
// compatibilidad con registros/formularios anteriores, aceptamos también
// valores ISO tomando únicamente su prefijo YYYY-MM-DD.
const normalizeExpenseDateStr = (expense = {}) => {
  const explicit = String(expense.dateStr || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(explicit)) return explicit;

  const raw = String(expense.date || "").trim();
  const prefix = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  return prefix ? prefix[1] : "";
};

const dateFromBusinessYMD = (ymd) => {
  const match = String(ymd || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, year, month, day] = match;
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  // Mediodía local evita que conversiones de zona horaria cambien de día.
  const result = new Date(y, m - 1, d, 12, 0, 0, 0);
  if (Number.isNaN(result.getTime())) return null;
  // Evita que JS normalice silenciosamente fechas imposibles (ej. 31/02).
  if (result.getFullYear() !== y || result.getMonth() !== m - 1 || result.getDate() !== d) return null;
  return result;
};

const COLLECTION = "gastos";

const AUTHORIZED_ADMIN_PAYROLL_EMAILS = new Set([
  "torresdeysi55@gmail.com",
  "andresplata35@gmail.com",
]);

const isPayrollExpense = (expense = {}) => {
  const source = String(expense.source || "").trim().toLowerCase();
  const category = String(expense.category || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
  return source === "payroll" || Boolean(expense.payrollReceiptId) || category === "nomina";
};


const ExpensesView = ({ onBack, currentUser }) => {
  const normalizedRole = String(currentUser?.role || "").trim().toLowerCase();
  const normalizedEmail = String(currentUser?.email || "").trim().toLowerCase();
  const canDeleteExpenses = hasAccess(currentUser, "expenses", "Eliminación");
  const canViewPayrollExpenses = normalizedRole === "ti" || (
    ["administrador", "admin"].includes(normalizedRole) &&
    AUTHORIZED_ADMIN_PAYROLL_EMAILS.has(normalizedEmail)
  );
  const [showForm, setShowForm] = useState(false);
  const [currentSubView, setCurrentSubView] = useState("list"); // 'list' | 'comparison'
  const [onlyMine, setOnlyMine] = useState(false); // filtro opcional
  const [confirmingExpense, setConfirmingExpense] = useState(null); // { id, category, amount, description }
  const [deletingExpense, setDeletingExpense] = useState(false);

  // 🔹 NUEVO: turno abierto (para amarrar gastos que afectan caja)
  const [openTurn, setOpenTurn] = useState(null);

  // Filtros UI
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setQDebounced(q), 250);
    return () => clearTimeout(id);
  }, [q]);
  const [dateFilter, setDateFilter] = useState(""); // "YYYY-MM-DD"
  const [category, setCategory] = useState("");   // categoría
  const [listPage, setListPage] = useState(1);


  // === Cargar turno abierto del usuario ===
  useEffect(() => {
    const loadOpenTurn = async () => {
      const who = currentUser?.email ?? currentUser?.username ?? currentUser ?? "";
      if (!who) return;

const qOpen = query(
          collection(db, "cajas"),
        where("user", "==", who),
        where("estado", "==", "abierta")
      );
const snap = await getDocs(qOpen);
      const first = snap.docs[0];
      setOpenTurn(first ? { id: first.id, ...first.data() } : null);
    };
    loadOpenTurn();
  }, [currentUser]);

  const {
    expenses: expensesData = [],
    loading: loadingExpenses,
    reload,
    upsertExpense,
    removeExpense,
  } = useExpensesData({
    startDate: null,
    endDate: null,
    affectsCash: null,
  });

      // === Guardar un gasto NUEVO en Firestore ===
  const handleSaveExpense = async (newExpense) => {
    const who = currentUser?.email ?? currentUser?.username ?? currentUser ?? "desconocido";

    if (newExpense.affectsCash) {
      if (!openTurn?.id) {
        alert("No hay turno abierto para asignar este gasto. Abre tu caja primero.");
        return;
      }
      const turnUser = openTurn?.user || "";
      if (turnUser && turnUser !== who) {
        alert("El turno abierto pertenece a otro usuario. No puedes afectar su caja.");
        return;
      }
    }

    const dateStr = normalizeExpenseDateStr(newExpense);
    const dateValue = dateFromBusinessYMD(dateStr);
    if (!dateStr || !dateValue) {
      alert("La fecha del gasto no es válida. Selecciona nuevamente la fecha.");
      return;
    }

    const payload = {
      category: newExpense.category || "Gasto",
      amount: Number(newExpense.amount || 0),
      description: newExpense.description || "",
      date: dateValue,
      dateStr,
      year: dateStr?.slice(0, 4) || "",
      month: dateStr?.slice(0, 7) || "",
      registeredBy: who,
      support: newExpense.support?.publicId
        ? {
            provider: "cloudinary",
            publicId: newExpense.support.publicId,
            bytes: newExpense.support.bytes || 0,
            format: newExpense.support.format || "",
            width: newExpense.support.width || 0,
            height: newExpense.support.height || 0,
            createdAt: serverTimestamp(),
          }
        : null,
      isRecurring: !!newExpense.isRecurring,
      recurrencePeriod: newExpense.recurrencePeriod || "mensual",
      affectsCash: !!newExpense.affectsCash,
      turnId: newExpense.affectsCash ? (newExpense.turnId || openTurn?.id || "") : "",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const expenseRef = await addDoc(collection(db, COLLECTION), payload);
    markExpensesCacheDirty("created");
    const localNow = Date.now();
    upsertExpense({
      id: expenseRef.id,
      ...payload,
      date: dateValue,
      support: payload.support ? { ...payload.support, createdAt: localNow } : null,
      createdAt: localNow,
      updatedAt: localNow,
    });
    try {
      await logAudit({ module: "Gastos", action: "expense.created", severity: "info", details: { amount: payload.amount, category: payload.category } }, currentUser);
    } catch {}
    setShowForm(false);
  };

  // === Actualizar un gasto (opcional) ===
  const handleEditExpense = async (id, partial) => {
    await updateDoc(doc(db, COLLECTION, id), {
      ...partial,
      updatedAt: serverTimestamp(),
    });
    markExpensesCacheDirty("updated");
    const currentExpense = expensesData.find((expense) => expense.id === id) || { id };
    upsertExpense({ ...currentExpense, ...partial, id, updatedAt: Date.now() });
    try {
      await logAudit({ module: "Gastos", action: "expense.updated", severity: "info", target: { id }, details: partial }, currentUser);
    } catch {}
  };
  const deleteSupportIfNeeded = async (expense) => {
    const publicId = expense?.support?.publicId || expense?.support?.public_id;
    if (!publicId) return;
    await deleteExpenseSupport(expense.id);
  };

  // === Eliminar un gasto (opcional) ===
  const handleDeleteExpense = async (expense) => {
    const id = typeof expense === "string" ? expense : expense?.id;
    if (!id) return;

    if (!canDeleteExpenses) {
      const err = new Error("No tienes permiso de Eliminación en el módulo Gastos.");
      err.code = "permission-denied";
      throw err;
    }

    // El soporte no debe impedir borrar el registro principal. Si Cloudinary falla,
    // conservamos el error en consola y eliminamos el gasto de Firestore.
    try {
      await deleteSupportIfNeeded(expense);
    } catch (supportError) {
      console.warn("No se pudo eliminar el soporte del gasto; se continuará con el registro.", supportError);
    }

    await deleteDoc(doc(db, COLLECTION, id));
    markExpensesCacheDirty("deleted");
    removeExpense(id);

    try {
      await logAudit(
        { module: "Gastos", action: "expense.deleted", severity: "critical", target: { id } },
        currentUser
      );
    } catch {}
  };

  const confirmDelete = (expense) => {
    if (!expense) return;
    setConfirmingExpense({
      id: expense.id,
      category: expense.category || "Gasto",
      amount: Number(expense.amount || 0),
      description: expense.description || "",
      registeredBy: expense.registeredBy || "",
      support: expense.support || null,
      supportFile: expense.supportFile || "",
    });
  };

  const cancelConfirmDelete = () => {
    if (deletingExpense) return;
    setConfirmingExpense(null);
  };

  const performDelete = async () => {
    if (!confirmingExpense?.id) return;
    setDeletingExpense(true);
    try {
      const target = expenses.find(e => e.id === confirmingExpense.id) || confirmingExpense;
      await handleDeleteExpense(target);
      setConfirmingExpense(null);
    } catch (err) {
      console.error("No se pudo eliminar el gasto", err);
      const code = String(err?.code || "").toLowerCase();
      const message = code.includes("permission-denied")
        ? "No tienes permiso de Eliminación en el módulo Gastos. Un administrador puede habilitarlo en Usuarios > Permisos > Gastos > Eliminación."
        : (err?.message || "No se pudo eliminar el gasto. Intenta nuevamente.");
      alert(message);
    } finally {
      setDeletingExpense(false);
    }
  };

  // 🔹 Alternar “Afecta caja” desde la tabla
const toggleAffectsCash = async (g) => {
   const next = !g.affectsCash;
   const who = currentUser?.email ?? currentUser?.username ?? currentUser ?? "";

   // 🔒 Si ya está amarrado a un turno NO lo reasignes nunca
   if (next && g.turnId) {
     // Verifica que la caja siga abierta (defensa extra)
   try {
    const cSnap = await getDoc(doc(db, "cajas", g.turnId));
    const estado = cSnap.exists() ? (cSnap.data()?.estado || "") : "";
    if (estado && estado !== "abierta") {
     alert("Este gasto pertenece a una caja cerrada. No puede volver a afectarla.");
      return;
    }
  } catch {}

     await handleEditExpense(g.id, { affectsCash: true, turnId: g.turnId });
     return;
   }

   // 🔒 Bloquear activar si no tienes turno abierto
   if (next && !g.turnId && !openTurn?.id) {
     alert("No tienes un turno abierto. Abre tu caja para poder marcar que afecte caja.");
     return;
   }
 const turnUser = openTurn?.user || "";
  if (turnUser && turnUser !== who) {
    alert("El turno abierto pertenece a otro usuario. No puedes afectar su caja.");
    return;
   }

   // 🔒 (Opcional) Sólo el creador puede cambiar su gasto
   // if (g.registeredBy && g.registeredBy !== who) {
   //   alert("Sólo el usuario que registró el gasto puede cambiar si afecta caja.");
   //   return;
   // }

   // ✅ Activar: asigna a mi turno abierto (si no tenía)
   if (next) {
     await handleEditExpense(g.id, {
       affectsCash: true,
       turnId: g.turnId || openTurn.id,
     });
     return;
   }

   // ✅ Desactivar: conserva turnId para no saltar de turno
 await handleEditExpense(g.id, {
   affectsCash: false,
   turnId: g.turnId || "",
 });
};

const handleViewSupport = async (expense) => {
  if (expense?.support?.publicId) {
    try {
      const data = await getExpenseSupportUrl(expense.id);
      window.open(data.signedUrl, '_blank', 'noopener');
      return;
    } catch (err) {
      alert(err?.message || 'No se pudo abrir el soporte.');
      return;
    }
  }

  if (expense?.supportFile) {
    window.open(expense.supportFile, '_blank', 'noopener');
  }
};

  // === Cálculos locales para la subvista 'comparison' ===
  // La restricción se aplica antes de totales, filtros, comparaciones y acciones.
  // Así un gasto de nómina no se revela indirectamente en ninguna subvista.
  const baseExpenses = useMemo(
    () => canViewPayrollExpenses
      ? expensesData
      : expensesData.filter((expense) => !isPayrollExpense(expense)),
    [expensesData, canViewPayrollExpenses]
  );
  const expenses = useMemo(() => {
    if (!onlyMine) return baseExpenses;
    const who = currentUser?.email ?? currentUser?.username ?? currentUser ?? "";
    return baseExpenses.filter((e) => (e.registeredBy || "").toLowerCase() === (who || "").toLowerCase());
  }, [baseExpenses, onlyMine, currentUser]);

  const monthlyExpenses = useMemo(() => {
    const acc = {};
    for (const e of expenses) {
      const key = e.month || (e.dateStr ? e.dateStr.slice(0, 7) : "N/A");
      acc[key] = (acc[key] || 0) + Number(e.amount || 0);
    }
    return Object.entries(acc).sort((a, b) => a[0].localeCompare(b[0]));
  }, [expenses]);

  const categoryExpenses = useMemo(() => {
    const acc = {};
    for (const e of expenses) {
      const key = e.category || "Sin categoría";
      acc[key] = (acc[key] || 0) + Number(e.amount || 0);
    }
    return Object.entries(acc).sort((a, b) => b[1] - a[1]);
  }, [expenses]);

// Catálogo de categorías desde la data
const categories = useMemo(() => {
  const set = new Set(expenses.map(e => (e.category || "").trim()).filter(Boolean));
  return Array.from(set).sort();
}, [expenses]);

// Filtrado local
const filtered = useMemo(() => {
  return expenses.filter(e => {
    const okDate = dateFilter ? getExpenseYMD(e) === dateFilter : true;
    const okCat   = category ? (e.category === category) : true;
    const text    = (e.category || "") + " " + (e.description || "") + " " + (e.registeredBy || "");
const okQ     = qDebounced ? text.toLowerCase().includes(qDebounced.toLowerCase()) : true;
    return okDate && okCat && okQ;
  });
}, [expenses, dateFilter, category, qDebounced]);

const LIST_PAGE_SIZE = 25;
const totalListPages = Math.max(1, Math.ceil(filtered.length / LIST_PAGE_SIZE));
const safeListPage = Math.min(listPage, totalListPages);
const paginatedExpenses = useMemo(() => {
  const start = (safeListPage - 1) * LIST_PAGE_SIZE;
  return filtered.slice(start, start + LIST_PAGE_SIZE);
}, [filtered, safeListPage]);
const listRangeStart = filtered.length ? (safeListPage - 1) * LIST_PAGE_SIZE + 1 : 0;
const listRangeEnd = filtered.length ? Math.min(safeListPage * LIST_PAGE_SIZE, filtered.length) : 0;

useEffect(() => {
  setListPage(1);
}, [dateFilter, category, qDebounced, onlyMine]);

useEffect(() => {
  if (listPage > totalListPages) setListPage(totalListPages);
}, [listPage, totalListPages]);

// Totales visibles
const totalFiltered = filtered.reduce((s, e) => s + Number(e.amount || 0), 0);
const totalAfectaCaja = filtered
  .filter(e => e.affectsCash)
  .reduce((s, e) => s + Number(e.amount || 0), 0);


  // === Render ===
  const renderSubView = () => {
    switch (currentSubView) {
      case "list":
        return (
 <div className="bg-white rounded-xl shadow-lg overflow-hidden">
  <div className="overflow-x-auto [scrollbar-gutter:stable]">
   <table className="w-full min-w-[980px] divide-y divide-gray-200">
    <thead className="bg-gray-50 sticky top-0 z-20">
                    <tr>
<th className="px-3 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider w-[96px] sticky left-0 z-30 bg-gray-50 border-r border-gray-200">Fecha</th>
<th className="px-3 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider w-[120px]">Categoría</th>
<th className="px-3 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider w-[110px]">Monto</th>
<th className="px-3 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider min-w-[180px]">Descripción</th>
<th className="px-3 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider w-[150px]">Registrado por</th>
<th className="px-3 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider w-[145px]">Afecta caja</th>
<th className="px-3 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider w-[100px]">Recurrente</th>
<th className="px-3 py-3 text-center text-[11px] font-medium text-gray-500 uppercase tracking-wider w-[78px] sticky right-[92px] z-30 bg-gray-50 border-l border-gray-200">Soporte</th>
<th className="px-3 py-3 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider w-[92px] sticky right-0 z-30 bg-gray-50 border-l border-gray-200">Acciones</th>
                  </tr>
                </thead>
<tbody className="bg-white divide-y divide-gray-200">
  {filtered.length > 0 ? (
    paginatedExpenses.map((e) => (
<tr key={e.id} className="odd:bg-white even:bg-gray-50/30 hover:bg-gray-50 transition">
<td className="px-4 py-2.5 whitespace-nowrap text-sm text-gray-500 sticky left-0 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 border-r border-gray-100">
{fmtDateCell(e)}
        </td>
 <td className="px-3 py-2.5 whitespace-nowrap text-sm">
            <span className="inline-flex max-w-full items-center px-2 py-1 rounded-full bg-gray-100 text-gray-700 truncate">
            {e.category || "—"}
          </span>
        </td>
<td className="px-3 py-2.5 whitespace-nowrap text-sm font-semibold tabular-nums">
            {fmtCOP(e.amount)}
        </td>
<td className="px-3 py-2.5 text-sm text-gray-600 max-w-[18rem] truncate" title={e.description || ""}>
              {e.description || "N/A"}
        </td>
<td className="px-3 py-2.5 text-sm text-gray-500">
            <div className="max-w-[9.5rem] truncate" title={e.registeredBy || ""}>{e.registeredBy || "—"}</div></td>

        {/* Toggle Afecta caja */}
 <td className="px-3 py-2.5 text-sm align-top">
            <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!e.affectsCash}
              onChange={() => toggleAffectsCash(e)}
              disabled={ !!e.turnId && e.turnId !== openTurn?.id }  // sólo puedes togglear si es de tu turno abierto o sin turnId
className="h-4 w-4 shrink-0 text-blue-600 rounded border-gray-300"
              title="Marcar si descuenta caja del turno"
            />
 <span className={`text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap ${e.affectsCash ? "bg-red-50 text-red-700 ring-1 ring-red-200" : "bg-gray-50 text-gray-600 ring-1 ring-gray-200"}`}>
    {e.affectsCash ? "Sí" : "No"}
</span>
          </label>
          { (!!e.turnId && e.turnId !== openTurn?.id) && (
   <div className="text-[11px] text-amber-600 mt-1 leading-tight max-w-[8.5rem]">Bloqueado: pertenece a otra caja</div>
 ) }
{e.affectsCash && (
 <div className="hidden 2xl:block text-[11px] text-gray-400 mt-1 truncate max-w-[8.5rem]" title={`turnId: ${e.turnId || "—"}`}>
    turnId: {e.turnId || "—"}
   </div>
)}  
       </td>

<td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-500" title={e.isRecurring ? `Sí (${e.recurrencePeriod || "mensual"})` : "No"}>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] ${e.isRecurring ? "bg-amber-50 text-amber-700 ring-1 ring-amber-200" : "bg-gray-50 text-gray-600 ring-1 ring-gray-200"}`}>
              {e.isRecurring ? "Sí" : "No"}
            </span>
        </td>

<td className="px-3 py-2.5 whitespace-nowrap text-sm text-center sticky right-[92px] z-10 bg-white border-l border-gray-100 group-even:bg-gray-50/30 group-hover:bg-gray-50">
            {(e.support?.publicId || e.supportFile) ? (
            <button
              type="button"
              onClick={() => handleViewSupport(e)}
 className="inline-flex items-center justify-center gap-1 px-2 py-1 rounded-lg border border-gray-200 hover:bg-gray-100">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828L18 9.828a4 4 0 10-5.656-5.656L5.757 10.76a6 6 0 108.486 8.486L20.5 13"/></svg>
                    <span className="hidden xl:inline">Ver</span>
            </button>
          ) : <span className="text-gray-400">—</span>}
        </td>

<td className="px-3 py-2.5 whitespace-nowrap text-right text-sm sticky right-0 z-10 bg-white border-l border-gray-100 group-even:bg-gray-50/30 group-hover:bg-gray-50">
  <button
    onClick={() => confirmDelete(e)}
    disabled={!canDeleteExpenses}
    title={canDeleteExpenses ? "Eliminar gasto" : "No tienes permiso de Eliminación en Gastos"}
    className={`inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg border transition text-sm w-full ${
      canDeleteExpenses
        ? "border-red-300 text-red-700 hover:bg-red-600 hover:text-white"
        : "border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed"
    }`}
  >
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3M4 7h16"/></svg>
    <span className="hidden xl:inline">{canDeleteExpenses ? "Eliminar" : "Bloq."}</span>
  </button>
        </td>
      </tr>
    ))
  ) : (
    <tr>
 <td colSpan="9" className="px-6 py-10 text-center">
  <div className="space-y-3">
   <p className="text-sm text-gray-500">No hay gastos con los filtros actuales.</p>
   <button
     onClick={() => setShowForm(true)}
     className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 shadow">
      + Crear gasto
    </button>
  </div>
</td>  
   </tr>
  )}
</tbody>
              </table>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 border-t border-gray-200 bg-white text-sm text-gray-500">
              <span>
                Mostrando <strong className="text-gray-700">{listRangeStart}-{listRangeEnd}</strong> de <strong className="text-gray-700">{filtered.length}</strong> registros · 25 por página
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setListPage((page) => Math.max(1, page - 1))}
                  disabled={safeListPage <= 1}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <span className="min-w-[110px] text-center text-gray-600">Página {safeListPage} de {totalListPages}</span>
                <button
                  type="button"
                  onClick={() => setListPage((page) => Math.min(totalListPages, page + 1))}
                  disabled={safeListPage >= totalListPages}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        );
      case "comparison":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Por Mes */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">Gastos por Mes</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mes</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    </tr>
                  </thead>
<tbody className="divide-y divide-gray-200">
                      {monthlyExpenses.length ? (
                      monthlyExpenses.map(([m, total]) => (
                        <tr key={m} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">{m}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {Number(total).toLocaleString("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 })}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="2" className="px-6 py-4 text-center text-sm text-gray-500">No hay datos.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Por Categoría */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">Gastos por Categoría</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categoría</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {categoryExpenses.length ? (
                      categoryExpenses.map(([cat, total]) => (
                        <tr key={cat} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">{cat}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {Number(total).toLocaleString("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 })}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="2" className="px-6 py-4 text-center text-sm text-gray-500">No hay datos.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const tabClass = (active) =>
    `inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition
    ${active
      ? "bg-blue-600 text-white shadow-md ring-1 ring-blue-500/30"
      : "bg-white text-gray-700 hover:bg-gray-100 ring-1 ring-gray-200"}`;

 return (<div className="max-w-[1600px] mx-auto px-3 lg:px-6 pt-8 md:pt-12 space-y-6">
  {/* Encabezado del módulo */}
<header
 className="grid grid-cols-1 md:grid-cols-3 items-center gap-2" >
  <div className="space-y-0.5">
 <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight scroll-mt-24">Gastos</h1>
     <p className="text-sm text-gray-500">
      Registra, filtra y compara los gastos de tu operación.
    </p>
  </div>

  {/* Estado de turno (opcional, informativo) */}
  <div className="hidden md:flex items-center gap-2">
    {openTurn ? (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 text-xs">
        Turno abierto
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-50 text-gray-600 ring-1 ring-gray-200 text-xs">
        Sin turno abierto
      </span>
    )}
  </div>
</header>
<hr className="h-px bg-gray-100 border-0 mt-2" />

  
  {/* Toolbar mejorada */}
<div className="space-y-3">
  <div className="flex flex-wrap items-center justify-between gap-3">
    {/* Izquierda: Volver + Solo mis gastos */}
    <div className="flex items-center gap-2 flex-wrap">
      <button
      onClick={onBack}
      className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 shadow-sm"
      title="Volver"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
      </svg>
      <span className="font-medium">Volver</span>
    </button>

    <label className="flex items-center gap-2 text-sm text-gray-600 select-none px-3 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 shadow-sm">
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-gray-300 text-blue-600"
        checked={onlyMine}
        onChange={() => setOnlyMine(v => !v)}
      />
      Solo mis gastos
    </label>
  </div>

  {/* Derecha: Tabs + Nuevo */}
  <div className="flex items-center justify-start lg:justify-end gap-3 relative z-0">
        {/* Segmented control */}
    <div className="inline-flex items-center rounded-xl bg-white p-1 shadow-sm ring-1 ring-gray-200 relative z-10 shrink-0">
      <button
        onClick={() => setCurrentSubView("list")}
        className={tabClass(currentSubView === "list")}
      >
        <span className="h-2 w-2 rounded-full bg-blue-500 shadow-inner" aria-hidden />
        Lista
      </button>
      <button
        onClick={() => setCurrentSubView("comparison")}
        className={tabClass(currentSubView === "comparison")}
      >
        <span className="h-2 w-2 rounded-full bg-amber-500 shadow-inner" aria-hidden />
        Comparación
      </button>
    </div>

    <button
      onClick={() => reload()}
      className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 shadow-sm text-sm text-gray-800"
      disabled={loadingExpenses}
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v6h6M20 20v-6h-6M5 19A9 9 0 0119 5" />
      </svg>
      {loadingExpenses ? "Actualizando..." : "Actualizar"}
    </button>

    {/* Nuevo gasto */}
    <button
      onClick={() => setShowForm(true)}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 shadow-md ring-1 ring-emerald-500/30"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v14m-7-7h14" />
      </svg>
      Nuevo gasto
    </button>
  </div>
  </div>

  {/* Centro: Filtros */}
  <div className="flex flex-col md:flex-row gap-2 md:items-center bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-3">
    <div className="relative flex-1 min-w-[220px]">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.3-4.3" />
        </svg>
      </span>
      <input
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder="Buscar (categoría, descripción, usuario)"
        className="pl-9 pr-3 py-2 rounded-xl border border-gray-200 bg-white w-full shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
      />
    </div>

    <input
      type="date"
      value={dateFilter}
      onChange={e => setDateFilter(e.target.value)}
      className="px-3 py-2 rounded-xl border border-gray-200 bg-white shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
      title="Filtrar por fecha"
    />

    <select
      value={category}
      onChange={e => setCategory(e.target.value)}
 className="px-3 py-2 rounded-xl border border-gray-200 bg-white shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500
          min-w-[220px] md:min-w-[240px] pr-8"
            title="Filtrar por categoría"
    >
      <option value="">Todas las categorías</option>
      {categories.map(c => <option key={c} value={c}>{c}</option>)}
    </select>
    {dateFilter ? (
      <button
        type="button"
        onClick={() => setDateFilter("")}
        className="px-3 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-sm text-gray-700"
      >
        Limpiar fecha
      </button>
    ) : null}
  </div>
</div>
<div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl shadow p-4">
 <p className="text-sm text-gray-500">Total (filtrado)</p>
  <p className="text-3xl font-extrabold tracking-tight">{fmtCOP(totalFiltered)}</p>
      </div>
      <div className="bg-white rounded-xl shadow p-4">
<p className="text-sm text-gray-500">Total que afecta caja</p>
    <p className="text-3xl font-extrabold text-red-600 tracking-tight">{fmtCOP(totalAfectaCaja)}</p>
      </div>
      <div className="bg-white rounded-xl shadow p-4">
<p className="text-sm text-gray-500">Registros</p>
   <p className="text-3xl font-extrabold tracking-tight">{filtered.length}</p>  
       </div>
    </div>

      {showForm ? (
        <div className="bg-white rounded-xl p-6 shadow">
          <ExpenseForm
            onSave={handleSaveExpense}
            onCancel={() => setShowForm(false)}
            currentUser={currentUser?.email ?? currentUser?.username ?? currentUser}
            openTurn={openTurn}              // 👈 para que el form pueda setear turnId si corresponde
            defaultAffectsCash={false}       // en módulo Gastos, por defecto NO afecta caja
          />
        </div>
      ) : null}

      {renderSubView()}

      {confirmingExpense ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl p-6 space-y-5">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <p className="text-lg font-semibold text-gray-900">Eliminar gasto</p>
                <p className="text-sm text-gray-600 mt-1">
                  Esta acción no se puede deshacer. El gasto dejará de aparecer en reportes, panel financiero y cierres.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between text-sm text-gray-700">
                <span className="text-gray-500">Categoría</span>
                <span className="font-semibold text-gray-900">{confirmingExpense.category}</span>
              </div>
              <div className="flex items-center justify-between text-sm text-gray-700">
                <span className="text-gray-500">Monto</span>
                <span className="font-semibold text-gray-900">{fmtCOP(confirmingExpense.amount)}</span>
              </div>
              {confirmingExpense.description ? (
                <div className="text-sm text-gray-700">
                  <span className="text-gray-500">Descripción: </span>
                  <span className="font-medium text-gray-900">{confirmingExpense.description}</span>
                </div>
              ) : null}
              {confirmingExpense.registeredBy ? (
                <div className="text-sm text-gray-700">
                  <span className="text-gray-500">Registrado por: </span>
                  <span className="font-medium text-gray-900">{confirmingExpense.registeredBy}</span>
                </div>
              ) : null}
            </div>

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={cancelConfirmDelete}
                disabled={deletingExpense}
                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
                onClick={performDelete}
                disabled={deletingExpense}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {deletingExpense ? "Eliminando..." : "Eliminar definitivamente"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ExpensesView;

